import React, { useId } from "react";
import { Dialog } from "@mui/material";
import Button from "../Button/Button";
import "./ConfirmDialog.css";

/**
 * Confirmation dialog — consolidates the duplicated Dialog + actions pattern in
 * ResetGameButton and DeleteGameButton (CopyGameButton is a future migration
 * target — it still uses a raw MUI Dialog with a form field).
 *
 * Brand-styled: the MUI Dialog supplies only the portal + backdrop + focus
 * trap; the Paper, type, and buttons are brand tokens (teal primary / red
 * destructive), reading the surface semantic vars. Because the dialog portals
 * to <body>, vars resolve at :root (the manager surface) — correct for the
 * admin actions these back. A `surface` prop can scope it later.
 *
 * Two ways to specify the buttons:
 *  - Simple (default): onCancel/onConfirm + cancelLabel/confirmLabel, with
 *    `destructive` toggling the confirm button red.
 *  - Flexible: pass `actions` — an ordered array of
 *    { label, onClick, variant?, loading?, disabled?, autoFocus? } — for a
 *    positive CTA confirm, or a 3-button layout (default + destructive +
 *    positive). `variant` is any Button variant (secondary / primary / cta /
 *    danger / ghost). When `actions` is given it replaces the simple pair.
 *
 * Props:
 *   open, onCancel, onConfirm
 *   title?, message (string or node)
 *   confirmLabel (default "Confirm"), cancelLabel (default "Cancel")
 *   destructive (bool) — red confirm button (simple mode)
 *   loading (bool) — disables the simple-mode buttons while the action runs
 *   actions? — see above
 *   children — extra content (e.g. a text field) rendered above the actions
 */
const PAPER_SX = {
  background: "var(--surface-card, #fff)",
  color: "var(--on-surface, #0f172a)",
  borderRadius: "var(--radius-lg, 12px)",
  boxShadow: "var(--shadow-pop, 0 8px 24px rgba(15,23,42,0.14))",
  maxWidth: 440,
  width: "100%",
};

const ConfirmDialog = ({
  open,
  onCancel,
  onConfirm,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  loading = false,
  actions,
  children,
}) => {
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const descId = `${baseId}-desc`;
  const resolvedActions = actions || [
    { label: cancelLabel, variant: "secondary", onClick: onCancel, disabled: loading },
    { label: confirmLabel, variant: destructive ? "danger" : "primary", onClick: onConfirm, loading, autoFocus: true },
  ];
  return (
  <Dialog
    open={open}
    onClose={loading ? undefined : onCancel}
    slotProps={{ paper: { className: "gx-confirm", sx: PAPER_SX } }}
    aria-labelledby={title ? titleId : undefined}
    aria-describedby={message ? descId : undefined}
  >
    <div className="gx-confirm-inner">
      {title && (
        <h2 id={titleId} className="gx-confirm-title">
          {title}
        </h2>
      )}
      {message && (
        <div id={descId} className="gx-confirm-msg">
          {message}
        </div>
      )}
      {children}
      <div className="gx-confirm-actions">
        {resolvedActions.map((a, i) => (
          <Button
            key={i}
            variant={a.variant || "primary"}
            onClick={a.onClick}
            loading={a.loading}
            disabled={a.disabled}
            autoFocus={a.autoFocus}
          >
            {a.label}
          </Button>
        ))}
      </div>
    </div>
  </Dialog>
  );
};

export default ConfirmDialog;
